<template>
  <div :class="$style.size_check_modal">
    <h3 :class="$style.modal_title">サイズを確認する</h3>
    <MoleculesConsumerStepper :class="$style.stepper">
      <AtomsConsumerStepperItem :active="state.step === 'input'"> 情報入力 </AtomsConsumerStepperItem>
      <AtomsConsumerStepperItem :active="state.step === 'size'"> 推奨サイズ </AtomsConsumerStepperItem>
    </MoleculesConsumerStepper>
    <div :class="$style.size_check_section">
      <!-- step1 情報入力 -->
      <template v-if="state.step === 'input' || isLoading">
        <div v-if="!isFootCategory" :class="$style.form_table">
          <MoleculesConsumerFormItem>
            <template #title>性別</template>
            <AtomsConsumerSelect
              v-model="state.form.selectedGender"
              placeholder="選択してください"
              :options="genderList"
              option-value="value"
              option-label="label"
              :class="$style.select"
            />
          </MoleculesConsumerFormItem>
          <MoleculesConsumerFormItem>
            <template #title>身長(cm)</template>
            <AtomsConsumerInputNumber v-model="state.form.bodyHeight" name="body_height" placeholder="170" min="0" />
          </MoleculesConsumerFormItem>

          <MoleculesConsumerFormItem>
            <template #title>体重(kg)</template>
            <AtomsConsumerInputNumber v-model="state.form.bodyWeight" name="body_weight" placeholder="60" min="0" />
          </MoleculesConsumerFormItem>
          <div :class="$style.button_wrap">
            <AtomsConsumerButton variant="primary" block :disabled="isLoading" @click="nextStep()">
              推奨サイズを確認する
            </AtomsConsumerButton>
            <AtomsConsumerButton variant="secondary" block @click="emit('close')"> 閉じる </AtomsConsumerButton>
          </div>
        </div>
        <div v-else :class="$style.form_table">
          <MoleculesConsumerFormItem>
            <template #title>足のサイズ（cm）</template>
            <AtomsConsumerSelect
              v-model="state.form.selectedFootSize"
              placeholder="選択してください"
              :options="footSizeList"
              option-value="value"
              option-label="label"
              :class="$style.select"
            />
          </MoleculesConsumerFormItem>
          <div :class="$style.button_wrap">
            <AtomsConsumerButton variant="primary" block :disabled="isLoading" @click="nextStep()">
              推奨サイズを確認する
            </AtomsConsumerButton>
            <AtomsConsumerButton variant="secondary" block @click="emit('close')"> 閉じる </AtomsConsumerButton>
          </div>
        </div>
      </template>
      <template v-else-if="state.step === 'size'">
        <div :class="$style.size_chart_body">
          <div v-if="!isOutOfRange" :class="$style.size_chart_contents">
            <h4 v-if="recommendSize" :class="$style.size_text"
              >おすすめサイズ: <span :class="$style.focus">{{ recommendSize }}</span
              >サイズ</h4
            >
            <h4 v-else :class="$style.size_text"
              >おすすめサイズが見つかりませんでしたが、周辺のサイズもご参考いただけると幸いです。</h4
            >
            <template v-if="!isFootCategory">
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th v-for="(height, index) in sizeChart.heights" :key="`height-${index}`"> {{ height }}cm~ </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(weight, rowIndex) in sizeChart.weights" :key="`weight-${rowIndex}`">
                    <th>{{ weight }}kg~</th>
                    <td
                      v-for="(size, colIndex) in sizeChart.apparelRecommendSizes[rowIndex]"
                      :key="`size-${rowIndex}-${colIndex}`"
                      :class="{ [$style.highlight]: isHighlight(rowIndex, colIndex) }"
                    >
                      {{ size }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
            <template v-else>
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th v-for="(footSize, index) in sizeChart.footSizes" :key="`size-${index}`"> {{ footSize }}cm </th>
                  </tr>
                </thead>
                <tbody :class="$style.foot_body">
                  <tr>
                    <th></th>
                    <td
                      v-for="(size, colIndex) in sizeChart.footRecommendSizes"
                      :key="`size-${colIndex}`"
                      :class="{ [$style.highlight]: isHighlight(0, colIndex) }"
                    >
                      {{ size }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
          </div>
          <p v-else
            >推奨サイズの取得に失敗しました。 <br />適切なサイズがわからない場合は<AtomsConsumerLink
              :url="buildContactURL()"
              >お問い合わせ</AtomsConsumerLink
            >頂くか、サイズチャートなどその他サイズに関するコンテンツをご確認ください。</p
          >
          <div :class="$style.button_wrap">
            <AtomsConsumerButton variant="secondary" block @click="state.step = 'input'"> 戻る </AtomsConsumerButton>
            <AtomsConsumerButton variant="secondary" block @click="emit('close')"> 閉じる </AtomsConsumerButton>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { sanitizeObject } from "@tential/ec-gql-schema/utils/functions/sanitize"
import { withQuery } from "ufo"
import { useToast } from "~/state"
import type {
  RecommendSizeChartResponse,
  RecommendSizeChartGender,
  RecommendSizeCharFootSize,
} from "~/types/server/api/recommend/size_chart"

type State = {
  step: "input" | "size"
  form: {
    selectedGender: RecommendSizeChartGender
    selectedFootSize?: RecommendSizeCharFootSize
    bodyHeight?: number
    bodyWeight?: number
  }
}
const state = ref<State>({
  step: "input",
  form: {
    selectedGender: "",
    bodyHeight: undefined,
    bodyWeight: undefined,
    selectedFootSize: undefined,
  },
})

type Props = {
  sizeChart: RecommendSizeChartResponse
  productName: string
  productId: string
  recommendRowIndex: number
  recommendColIndex: number
  recommendSize: string
  isFootCategory: boolean
  isLoading: boolean
}
const props = defineProps<Props>()

type Emits = {
  (
    e: "check",
    gender: RecommendSizeChartGender,
    height?: number,
    weight?: number,
    footSize?: RecommendSizeCharFootSize,
  ): Promise<void>
  (e: "close"): void
}
const emit = defineEmits<Emits>()

const genderList: { value: RecommendSizeChartGender; label: string }[] = [
  { value: "male", label: "男性" },
  { value: "female", label: "女性" },
]

const footSizeList: { value?: RecommendSizeCharFootSize; label: string }[] = [
  { value: undefined, label: "選択してください" },
  { value: 21.5, label: "21.5cm" },
  { value: 22.0, label: "22cm" },
  { value: 22.5, label: "22.5cm" },
  { value: 23.0, label: "23cm" },
  { value: 23.5, label: "23.5cm" },
  { value: 24.0, label: "24cm" },
  { value: 24.5, label: "24.5cm" },
  { value: 25.0, label: "25cm" },
  { value: 25.5, label: "25.5cm" },
  { value: 26.0, label: "26cm" },
  { value: 26.5, label: "26.5cm" },
  { value: 27.0, label: "27cm" },
  { value: 27.5, label: "27.5cm" },
  { value: 28.0, label: "28cm" },
  { value: 28.5, label: "28.5cm" },
  { value: 29.0, label: "29cm" },
]

const toast = useToast()

const isOutOfRange = computed(() =>
  props.isFootCategory
    ? props.sizeChart.footRecommendSizes.length === 0
    : props.sizeChart.heights.length === 0 || props.sizeChart.weights.length === 0,
)

const isHighlight = (rowIndex: number, colIndex: number) => {
  return props.isFootCategory
    ? colIndex === props.recommendColIndex
    : rowIndex === props.recommendRowIndex && colIndex === props.recommendColIndex
}

const nextStep = async (): Promise<void> => {
  if (!props.isFootCategory) {
    if (!state.value.form.selectedGender || !state.value.form.bodyHeight || !state.value.form.bodyWeight) {
      toast?.showErrorToasted("全ての項目を入力してください")
      return
    }
  } else if (!state.value.form.selectedFootSize) {
    toast?.showErrorToasted("足のサイズを入力してください")
    return
  }
  state.value.form = sanitizeObject(state.value.form)
  await emit(
    "check",
    state.value.form.selectedGender,
    state.value.form.bodyHeight,
    state.value.form.bodyWeight,
    state.value.form.selectedFootSize,
  )
  state.value.step = "size"
}

const buildContactURL = () => {
  return withQuery("/contact", {
    size_check: true,
    product_name: encodeURIComponent(props.productName),
    product_id: encodeURIComponent(props.productId),
    height: encodeURIComponent(state.value.form.bodyHeight ?? ""),
    weight: encodeURIComponent(state.value.form.bodyWeight ?? ""),
    foot_size: encodeURIComponent(state.value.form.selectedFootSize ?? ""),
    gender: encodeURIComponent(state.value.form.selectedGender),
  })
}
</script>

<style lang="scss" scoped module>
a {
  color: $primary;
  text-decoration: underline;
}
.size_check_modal {
  max-width: $max-width-sm;
  width: calc(100vw - 2rem);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 1rem;
  @include md {
    padding: 2rem 4rem;
  }
  .stepper {
    padding-right: 0;
    padding-left: 0;
  }
  .modal_title {
    color: $primary;
    margin-bottom: 1rem;
    > span {
      font-size: $font-size-12;
      font-weight: normal;
    }
  }
  .size_check_section {
    width: 100%;
    .button_wrap {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-top: 2rem;
    }
    .size_chart_body {
      .size_text {
        color: $primary;
        font-weight: bold;
        font-size: $font-size-12;
        margin-bottom: 1rem;
        .focus {
          font-size: $font-size-16;
        }
      }
      table {
        min-width: 290px;
        max-width: 500px;
        width: 100%;
        margin: 0 auto;
        border-collapse: collapse;
        font-size: 14px;
        overflow-x: scroll;
      }

      th,
      td {
        border: 1px solid $white;
        text-align: center;
        padding: 20px 0;
        font-weight: normal;
      }

      thead th {
        background-color: $white;
        padding: 0 0 10px 0;
      }

      tbody th {
        width: 50px;
        text-align: right;
        padding: 0 10px 0 0;
        background-color: $white;
        color: #4a4a4a;
      }

      .foot_body th {
        padding: 0;
        width: 0;
      }

      td {
        background-color: rgba(40, 75, 125, 0.2);
      }

      .highlight {
        background-color: $primary;
        color: $white;
        font-weight: bold;
      }
    }
  }
}
</style>
